import { useEffect, useRef, useState, type PointerEvent } from "react";
import { NotoReactionFace, QUICK_REACTIONS } from "@/lib/chatNotoEmoji";

const TAP_SLOP = 14;
const ARM_MS = 420;

function Chevron({ dir }: { dir: "left" | "right" }) {
  const left = dir === "left";
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d={left ? "M15.75 19.5L8.25 12l7.5-7.5" : "M8.25 4.5l7.5 7.5-7.5 7.5"}
      />
    </svg>
  );
}

type Props = {
  myReaction?: string | null;
  compact?: boolean;
  /** Timestamp d’ouverture du menu — ignore le tap fantôme après un appui long. */
  openedAt?: number;
  onReact: (emoji: string) => void;
};

export function ChatQuickReactionStrip({
  myReaction,
  compact = false,
  openedAt = 0,
  onReact,
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tapRef = useRef<{ x: number; y: number; scroll: number } | null>(null);
  const draggedRef = useRef(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  function updateEdges() {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 6);
    setCanRight(max > 6 && el.scrollLeft < max - 6);
  }

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateEdges();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateEdges) : null;
    ro?.observe(el);
    el.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      ro?.disconnect();
      el.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
    };
  }, []);

  function nudge(dir: number) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.72), behavior: "smooth" });
  }

  function beginTap(e: PointerEvent<HTMLButtonElement>) {
    e.stopPropagation();
    tapRef.current = {
      x: e.clientX,
      y: e.clientY,
      scroll: scrollerRef.current?.scrollLeft ?? 0,
    };
    draggedRef.current = false;
  }

  function moveTap(e: PointerEvent<HTMLButtonElement>) {
    const start = tapRef.current;
    if (!start) return;
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > TAP_SLOP) {
      draggedRef.current = true;
    }
  }

  function endTap(emoji: string) {
    const start = tapRef.current;
    tapRef.current = null;
    if (!start || draggedRef.current) return;
    if (openedAt && Date.now() - openedAt < ARM_MS) return;
    const scroll = scrollerRef.current?.scrollLeft ?? 0;
    if (Math.abs(scroll - start.scroll) > 4) return;
    onReact(emoji);
  }

  const fade = [
    "chat-emoji-strip",
    compact ? "chat-emoji-strip--compact" : "",
    canLeft ? "chat-emoji-strip--fade-left" : "",
    canRight ? "chat-emoji-strip--fade-right" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={fade}
      role="toolbar"
      aria-label="Réactions rapides"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="chat-emoji-strip__chev"
        aria-label="Voir les emojis précédents"
        disabled={!canLeft}
        onClick={() => nudge(-1)}
      >
        <Chevron dir="left" />
      </button>
      <div className="chat-emoji-strip__scroller" ref={scrollerRef}>
        {QUICK_REACTIONS.map((emoji) => {
          const active = myReaction === emoji;
          return (
            <button
              key={emoji}
              type="button"
              className={`chat-emoji-strip__btn${active ? " is-active" : ""}`}
              title={active ? "Retirer la réaction" : "Réagir"}
              onPointerDown={beginTap}
              onPointerMove={moveTap}
              onPointerUp={() => endTap(emoji)}
              onPointerCancel={() => {
                tapRef.current = null;
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <NotoReactionFace emoji={emoji} />
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="chat-emoji-strip__chev"
        aria-label="Voir les emojis suivants"
        disabled={!canRight}
        onClick={() => nudge(1)}
      >
        <Chevron dir="right" />
      </button>
    </div>
  );
}
